import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { CompanyFilter } from "../../../../../legacy/types/companyFilter";
import VendorsQueryPayload from "../../../../../types/taxonomy";
import { VENDOR_ID_QUERY } from "../../../../../apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "../../../../../utils/consts";
import {
  getExcludedFilterValue,
  getIncludedFilterValues,
  generateInclusionLabel,
  flatInclusionFilterIds,
} from "../utils";

// Helper to transform vendor data into a name map
const vendorDataToNameMap = (
  vendors: VendorsQueryPayload["vendor"]["data"] | undefined
): Record<string, string> => {
  if (!vendors) return {};

  return vendors.reduce(
    (acc, vendor) => {
      if (vendor && vendor.id && vendor.name) {
        acc[String(vendor.id)] = vendor.name;
      }
      return acc;
    },
    {} as Record<string, string>
  );
};

export const useVendorsLabel = (filter: CompanyFilter["vendors"]) => {
  const includeFilters = useMemo(
    () => getIncludedFilterValues(filter || []),
    [filter]
  );
  const excludeFilter = useMemo(
    () => getExcludedFilterValue(filter || []),
    [filter]
  );

  const includedIds = useMemo(
    () => flatInclusionFilterIds(includeFilters).map(Number),
    [includeFilters]
  );
  const excludedIds = useMemo(
    () => excludeFilter?.ids?.map(Number) || [],
    [excludeFilter]
  );

  const { data: vendorQueryData, loading: vendorsLoading } =
    useQuery<VendorsQueryPayload>(VENDOR_ID_QUERY, {
      variables: {
        ids: [...includedIds, ...excludedIds],
      },
      skip: includedIds.length === 0 && excludedIds.length === 0,
      context: {
        clientName: TAXONOMY_CLIENT,
      },
    });

  const nameMap = useMemo(
    () => vendorDataToNameMap(vendorQueryData?.vendor.data),
    [vendorQueryData]
  );

  // If there are IDs but names are still loading, return a placeholder or empty
  if ((includedIds.length > 0 || excludedIds.length > 0) && vendorsLoading) {
    return "Loading..."; // Or an empty string: ""
  }

  return generateInclusionLabel(includedIds, excludedIds, nameMap);
};
